'use client';

import { useFormContext } from 'react-hook-form';
import { RadioGroup, RadioGroupItem } from '@eliadmin/core/src/components/ui/radio-group';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import type { SparepartsFormValues } from './spareparts-wrapper';
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@eliadmin/core/src/components/ui/form';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { X } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { ModelField } from './model-field';
import { BrandSelection } from './brand-selection';
import type { Model } from '../../api/spareparts-api';
import { searchByVin } from '../../api/spareparts-api';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { Spinner } from '@eliadmin/core/src/components/ui/spinner';
import { useSearchParams, useRouter, usePathname } from 'next/navigation';

interface SparepartsFormProps {
  className?: string;
  selectedModel: Model | null;
  setSelectedModel: (model: Model | null) => void;
}

export function SparepartsForm({
  className,
  selectedModel,
  setSelectedModel,
}: SparepartsFormProps) {
  const { t } = useTranslations();
  const form = useFormContext<SparepartsFormValues>();
  const [filledField, setFilledField] = useState<
    'vin' | 'engine_number' | 'model' | 'modelName' | null
  >(null);
  const [isSearching, setIsSearching] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const apiKeys = useGlobalStore(state => state.apiKeys);
  // Use the spareparts API key from global store
  const TEST_API_KEY = (apiKeys as any)?.spareparts || '';

  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();
  const modelArticleNumber = searchParams.get('modelArticleNumber');

  // Watch all three fields to determine which one is filled
  const vinValue = form.watch('vin');
  const engineNumberValue = form.watch('engine_number');
  const modelValue = form.watch('model');
  const selectedBrand = form.watch('selectedBrand');
  const brandId = selectedBrand?.id || form.watch('brandId');

  useEffect(() => {
    if (vinValue && vinValue?.trim?.()) {
      setFilledField('vin');
    } else if (engineNumberValue && engineNumberValue?.trim?.()) {
      setFilledField('engine_number');
    } else if (modelValue) {
      setFilledField('model');
    } else {
      setFilledField(null);
    }
  }, [vinValue, engineNumberValue, modelValue]);

  const ignoredArticleNumberRef = useRef<string | null>(null);
  const searchRequestRef = useRef(0);

  useEffect(
    () => () => {
      searchRequestRef.current += 1;
    },
    [],
  );

  // The current API cannot resolve a bare article number to its model-tree node.
  // Preserve a selected model, but do not replace it with a guessed full-text result.
  const needsModelSelection =
    !!modelArticleNumber &&
    !selectedModel &&
    ignoredArticleNumberRef.current !== modelArticleNumber;

  const handleBrandChange = () => {
    // Ignore searches for the previous brand, including an old deep-link request.
    searchRequestRef.current += 1;
    ignoredArticleNumberRef.current = modelArticleNumber;
    setIsSearching(false);
    setNotFound(false);
    setSearchError(null);
    setFilledField(null);
    form.setValue('model', null);
    form.setValue('modelName', '');
    form.setValue('vin', '');
    form.setValue('engine_number', '');
    setSelectedModel(null);
    const nextSearchParams = new URLSearchParams(searchParams.toString());
    nextSearchParams.delete('modelArticleNumber');
    router.replace(`${pathname}?${nextSearchParams.toString()}`, { scroll: false });
  };

  const clearField = (fieldName: 'vin' | 'engine_number' | 'model' | 'modelName') => {
    searchRequestRef.current += 1;
    setIsSearching(false);
    setSearchError(null);
    setNotFound(false);
    form.setValue(fieldName, '');
    setFilledField(null);
  };

  const isFieldDisabled = (fieldName: 'vin' | 'engine_number' | 'model' | 'modelName') => {
    return filledField && filledField !== fieldName;
  };

  // Search function
  const handleSearch = async () => {
    const requestId = ++searchRequestRef.current;
    setIsSearching(true);
    setNotFound(false);
    setSearchError(null);

    try {
      const modelValue = form.watch('model');
      const vinValue = form.watch('vin');
      const engineNumberValue = form.watch('engine_number');

      if (modelValue) {
        // If model is provided, just log the selected model
        // Create a minimal model object for display
        const modelObj: Model = modelValue;
        setSelectedModel(modelObj);
        setNotFound(false);
      } else if (vinValue || engineNumberValue) {
        // If no model but VIN or engine number provided, search by VIN
        const searchResult = await searchByVin({
          body: {
            identificationNumber: vinValue || engineNumberValue,
            vin: vinValue,
            engineNumber: engineNumberValue,
          },
          apiKey: TEST_API_KEY,
        });
        if (requestId !== searchRequestRef.current) return;

        if (!searchResult.success) {
          if (searchResult.status === 404) {
            setNotFound(true);
            return;
          }
          throw new Error(searchResult.message || 'Vyhľadávanie sa nepodarilo. Skúste to znova.');
        }

        if (searchResult.success && searchResult.data) {
          // Handle the case where searchResult.data might be an array
          const searchResults = Array.isArray(searchResult.data)
            ? searchResult.data
            : [searchResult.data];

          // Find the leaf model (the one with hasChildren: false)
          const leafModel = searchResults.find(model => !model.hasChildren);

          if (leafModel) {
            // VIN lookup is not brand-filtered; do not retain a previously chosen brand.
            form.setValue('selectedBrand', null);
            form.setValue('brandId', leafModel.brandId || '');
            setSelectedModel(leafModel);

            // Set the found model in the form
            form.setValue('model', leafModel);
            form.setValue(
              'modelName',
              leafModel.description || leafModel.articleNumber || leafModel.id,
            );

            // Clear VIN and Engine Number fields
            form.setValue('vin', '');
            form.setValue('engine_number', '');

            // Update filled field state
            setFilledField('model');
            setNotFound(false);
          } else {
            setSelectedModel(null);
            setNotFound(true);
          }
        } else {
          setSelectedModel(null);
          setNotFound(true);
        }
      }
    } catch (error) {
      if (requestId !== searchRequestRef.current) return;
      setSearchError(error instanceof Error ? error.message : 'Vyhľadávanie sa nepodarilo.');
    } finally {
      if (requestId === searchRequestRef.current) setIsSearching(false);
    }
  };

  return (
    <div className={cn('gap-y-6 py-10 ', className)}>
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        {/* Brand Selection - Added above category */}
        <div className="col-span-2">
          <BrandSelection onBrandChange={handleBrandChange} />
        </div>
        {needsModelSelection && (
          <p
            role="status"
            className="col-span-2 rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
          >
            Model z uloženého odkazu sa nedá automaticky dohľadať podľa kódu. Vyberte značku a model
            v katalógu alebo použite VIN.
          </p>
        )}

        {/* Category Field */}
        <FormField
          control={form.control}
          name="category"
          render={({ field }) => (
            <FormItem className="col-span-2 space-y-2">
              <FormLabel className="text-sm font-medium">{t('Category')}</FormLabel>
              <FormControl>
                <RadioGroup
                  value={field.value}
                  onValueChange={field.onChange}
                  className="flex flex-row space-x-6"
                >
                  <div className="flex items-center space-x-3">
                    <RadioGroupItem
                      value="engine"
                      id="engine"
                      className={cn(
                        'h-[21px] w-[21px] rounded-full border-2 border-gray-300 text-primary',
                        'data-[state=checked]:border-primary data-[state=checked]:bg-primary',
                        'focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2',
                      )}
                    />
                    <label
                      htmlFor="engine"
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      {t('Engine')}
                    </label>
                  </div>
                  <div className="flex items-center space-x-3">
                    <RadioGroupItem
                      value="frame"
                      id="frame"
                      className={cn(
                        'h-[21px] w-[21px] rounded-full border-2 border-gray-300 text-primary',
                        'data-[state=checked]:border-primary data-[state=checked]:bg-primary',
                        'focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2',
                      )}
                    />
                    <label
                      htmlFor="frame"
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      {t('Frame')}
                    </label>
                  </div>
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Model Field - Using separate component */}
        <ModelField
          key={brandId || 'no-brand'}
          isDisabled={!brandId || isFieldDisabled('model') || false}
          onClear={() => {
            clearField('model');
            clearField('modelName');
          }}
          selectedModel={selectedModel}
          setSelectedModel={model => {
            searchRequestRef.current += 1;
            if (!model) ignoredArticleNumberRef.current = modelArticleNumber;
            setIsSearching(false);
            setNotFound(false);
            setSearchError(null);
            setSelectedModel(model);
            setFilledField(model ? 'model' : null);

            // Set search params with model.articleNumber if available
            if (model?.articleNumber) {
              const newSearchParams = new URLSearchParams(searchParams.toString());
              newSearchParams.set('modelArticleNumber', model.articleNumber);
              // Use replace with scroll: false to prevent page scroll
              router.replace(`${pathname}?${newSearchParams.toString()}`, { scroll: false });
            }
          }}
        />
        {!brandId && (
          <p className="col-span-2 text-sm text-gray-600">
            Pre výber modelu najprv vyberte značku. Podľa VIN môžete hľadať aj bez nej.
          </p>
        )}

        {/* VIN Field */}
        <FormField
          control={form.control}
          name="vin"
          render={({ field }) => (
            <FormItem className="col-span-2 sm:col-span-1">
              <FormLabel className="text-sm font-medium">{t('VIN')}</FormLabel>
              <FormControl>
                <div className="relative">
                  <Input
                    placeholder={t('Enter VIN number')}
                    disabled={isFieldDisabled('vin') || false}
                    className={{
                      wrapper: cn(
                        isFieldDisabled('vin') && 'cursor-not-allowed bg-gray-100 opacity-50',
                      ),
                      main: cn(
                        isFieldDisabled('vin') && 'cursor-not-allowed bg-gray-100 opacity-50',
                      ),
                    }}
                    {...field}
                  />
                  {field.value && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-2 top-1/2 h-6 w-6 -translate-y-1/2 p-0"
                      onClick={() => clearField('vin')}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Engine Number Field */}
        <FormField
          control={form.control}
          name="engine_number"
          render={({ field }) => (
            <FormItem className="col-span-2 sm:col-span-1">
              <FormLabel className="text-sm font-medium">{t('Engine number')}</FormLabel>
              <FormControl>
                <div className="relative">
                  <Input
                    placeholder={t('Enter engine number')}
                    disabled={isFieldDisabled('engine_number') || false}
                    className={{
                      wrapper: cn(
                        isFieldDisabled('engine_number') &&
                          'cursor-not-allowed bg-gray-100 opacity-50',
                      ),
                      main: cn(
                        isFieldDisabled('engine_number') &&
                          'cursor-not-allowed bg-gray-100 opacity-50',
                      ),
                    }}
                    {...field}
                  />
                  {field.value && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-2 top-1/2 h-6 w-6 -translate-y-1/2 p-0"
                      onClick={() => clearField('engine_number')}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Search Button */}
        <div className="col-span-2">
          <Button
            type="button"
            onClick={handleSearch}
            disabled={
              isSearching || (!modelValue && !vinValue?.trim() && !engineNumberValue?.trim())
            }
            className="w-full"
            variant="default"
          >
            {isSearching ? (
              <div className="flex items-center gap-2">
                <Spinner className="text-primary" />
                Searching models...
              </div>
            ) : (
              'Search'
            )}
          </Button>
        </div>

        {/* Display not found message in the UI */}
        {searchError && (
          <div role="alert" className="col-span-2 mt-2 text-sm text-red-700">
            {searchError}
          </div>
        )}
        {notFound && (
          <div role="status" className="col-span-2 mt-2 text-sm text-gray-600">
            Pre zadané číslo sa nenašiel model.
          </div>
        )}
      </div>
    </div>
  );
}
